import { Clipboard } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '@core/auth.service';
import { PartneroService } from '@core/services/partnero.service';
import { UserService } from '@core/services/user.service';
import { provideIcons } from '@ng-icons/core';
import { heroAdjustmentsVertical, heroBanknotes } from '@ng-icons/heroicons/outline';
import { TranslateModule } from '@ngx-translate/core';
import { UserInfo } from '@shared/models/user-info';
import { EMPTY, Subject, debounceTime, switchMap, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { RouteSearchComponent } from '../route-search/route-search.component';
import { BOTTOM_NAVIGATION_LIST, ISidebarItem, SIDEBAR_NAVIGATION_LIST } from './sidebar.helper';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [CommonModule, RouterModule, TranslateModule, ReactiveFormsModule, MatIconModule, RouteSearchComponent],
  providers: [
    provideIcons({
      heroAdjustmentsVertical,
      heroBanknotes,
    }),
  ],
  styleUrl: './sidebar.component.scss',
})
export class SidebarComponent implements OnInit, OnDestroy {
  router = inject(Router);
  private clipboard = inject(Clipboard);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private partneroService = inject(PartneroService);
  readonly open = input<boolean>(false);
  readonly sidebarClosed = output<void>();
  public readonly sidebarNavigationList: ISidebarItem[] = SIDEBAR_NAVIGATION_LIST;
  public readonly bottomNavigationList: ISidebarItem[] = BOTTOM_NAVIGATION_LIST;
  public greeting = '';
  public userName = '';
  public onlyProperties = false;
  public referralUrl = '';
  public form: FormGroup = new FormGroup({
    search: new FormControl(null),
  });
  public showCopyConfirmation = false;
  private destroy$ = new Subject<void>();
  private lastReferralLookupEmail = '';

  constructor() {
    this.greeting = this.getGreeting();
    this._formSubscription();
  }

  ngOnInit(): void {
    this.subscribeToUserInfo();
  }

  closeSidebar() {
    this.sidebarClosed.emit();
  }

  public getGreeting(): string {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return 'GREETINGS.MORNING';
    } else if (currentHour < 18) {
      return 'GREETINGS.AFTERNOON';
    } else {
      return 'GREETINGS.EVENING';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _formSubscription(): void {
    this.form
      .get('search')
      ?.valueChanges.pipe(debounceTime(500))
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        console.log(value);
      });
  }

  copyReferralLink() {
    if (this.referralUrl) {
      this.clipboard.copy(this.referralUrl);
      this.showCopyConfirmation = true;
      setTimeout(() => {
        this.showCopyConfirmation = false;
      }, 1500);
    }
  }

  onNavigationClick() {
    this.closeSidebar();
  }

  private subscribeToUserInfo(): void {
    this.authService.isAuthenticated$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(isAuthenticated => {
          if (!isAuthenticated) {
            this.resetUserState();
            return EMPTY;
          }

          return this.userService.getUserInfo();
        })
      )
      .subscribe(user => {
        if (!user?.email) {
          return;
        }

        this.userName = user.firstName ?? '';
        this.onlyProperties = this.isOnlyPropertiesUser(user);
        this.loadReferralLink(user.email);
      });
  }

  private loadReferralLink(email: string): void {
    if (!email || email === this.lastReferralLookupEmail) {
      return;
    }

    this.lastReferralLookupEmail = email;
    this.partneroService
      .getPartneroCustomer(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.referralUrl = response?.data?.referral_link ?? '';
        },
        error: () => {
          this.referralUrl = '';
        },
      });
  }

  private isOnlyPropertiesUser(user: UserInfo): boolean {
    const activeSubscriptions = user.subscriptionsHistory?.filter(subscription => subscription.isActive) ?? [];
    const hasPropertiesSubscription = activeSubscriptions.some(
      subscription => subscription.stripePriceId === environment.subscriptionProducts.properties
    );

    return hasPropertiesSubscription || activeSubscriptions.length === 0;
  }

  private resetUserState(): void {
    this.userName = '';
    this.onlyProperties = false;
    this.referralUrl = '';
    this.lastReferralLookupEmail = '';
  }
}
